import {ZoomInMapTwoTone} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import {
  Box,
  BoxProps,
  Button,
  Divider,
  Menu,
  MenuItem,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import {LatLng, LatLngBounds, latLngBounds} from 'leaflet';
import {MousePosition} from 'leaflet.mouseposition.ts/dist/src';
import _ from 'lodash';
import {Fragment, useEffect, useMemo, useState} from 'react';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Tooltip,
  useMapEvents,
} from 'react-leaflet';
import {useSelector} from 'react-redux';
import {useComponentSize} from 'react-use-size';

import {mineMapBaseUrl} from '../../../api/urls';
import {
  useConfiguration,
  useConfigurations,
} from '../../../hooks/configuration';
import {useGetAmsIconName} from '../../../hooks/get-ams-icon-name';
import {useAppSelector} from '../../../hooks/redux';
import {AlarmModuleNode} from '../../../interfaces/AlarmModuleNode';
import {AmsEmoduleInstallationHistoryItem} from '../../../interfaces/AmsEmoduleInstallationHistory';
import {AMSEModuleNode} from '../../../interfaces/AMSEModuleNode';
import {
  beltAddressMask,
  BeltMonitoringNode,
} from '../../../interfaces/BeltMonitoringNode';
import {CommtracNode} from '../../../interfaces/CommtracNode';
import {CommtracNodeByCnEvent} from '../../../interfaces/CommtracNodeByCnEvent';
import {CommtracNodeHistory} from '../../../interfaces/CommtracNodeHistory';
import {GasMonitoringNode} from '../../../interfaces/GasMonitoringNode';
import {MapCountByLayer} from '../../../interfaces/Map';
import {Node} from '../../../interfaces/Node';
import reduxSelectors from '../../../redux/selectors';
import {
  ALARM_MODULE_COLORS,
  ALARM_MODULE_TYPES,
} from '../../../utils/alarm-modules';
import {generatePickColorSet} from '../../../utils/heatmap';
import {COLORED_LEAFLET_ICONS, iconName} from '../../../utils/icons';
import {fetchMapConfig, saveMapConfig} from '../../../utils/map';
import {
  getAMSLocationHistoryId,
  getCommtracNodeByCnHistoryId,
  getCommtracNodeHistoryId,
} from '../../../utils/message-types';
import {createArrowIcon, findTimingOrRoutingGroup} from '../../../utils/nodes';
import {HazardHeatMapData} from '../../dashboard-panels/HazardHeatmap/HazardHeatmapGrid';
import {HeatMapLayer} from '../../dashboard-panels/HazardHeatmap/HeatMapLayer';
import AlarmTooltipContent from './AlarmTooltipContent';
import {ArrowLine} from './ArrowLine';
import CommtracNodeHistoryTooltipContent from './CommtracNodeHistoryTooltipContent';
import TempItemMarker from './TempItemMarker';

export type MapPanelType =
  | 'assets_tracking_report'
  | 'employee_tracking_report'
  | 'alarm_log_report'
  | 'cn_report'
  | 'nodes_report'
  | 'cn_tracking_report'
  | 'warning_hazard_report'
  | 'detection_log_report'
  | 'heatmap_report'
  | 'heatmap_event_logs'
  | 'ams_short_term_tracking_report'
  | 'ams_long_term_tracking_report'
  | 'ams_location_tracking_report'
  | 'ams_emodule_installation_history'
  | 'ams_sensor_nodes_report'
  | 'belt_nodes_report'
  | 'belt_location_tracking_report'
  | undefined;

export interface MapEmbedConfiguration {
  zoom: number;
  bounds: [[number, number], [number, number]];
  panel?: MapPanelType;
  autoFocus: boolean;
}

interface Props extends BoxProps {
  panel?: MapPanelType;
  heatMap?: HazardHeatMapData[];
  countByLayer?: MapCountByLayer[];
  eModules?: AMSEModuleNode[];
  beltsNodes?: BeltMonitoringNode[];
  ////////////
  beltLocationHistoryData?: any;
  ////////////
  alarmNodes?: AlarmModuleNode[];
  commtracNodes?: CommtracNode[];
  nodes?: Node[] | null;
  amsSensorNodes?: GasMonitoringNode[];
  employeeHistoryData?: CommtracNodeHistory[] | null;
  assetHistoryData?: CommtracNodeHistory[] | null;
  commtracNodeByCnEventsData?: CommtracNodeByCnEvent[] | null;
  amsLocationHistoryData?: GasMonitoringNode[] | null;
  amsEmoduleInstallationHistoryData?: AmsEmoduleInstallationHistoryItem[];
  selectedMapLayers?: MapLayerId[];
  availableMapLayers?: MapLayerId[];
  selectedLevel?: number | null;
  disableEditMode?: boolean;
  onSelectMapLayers?: (value: MapLayerId[]) => void;
  onSelectLevel?: (value: number | null) => void;
  onGetClickCoordinates?: (value: MapLatLangCoordinates) => void;
  onOpenItem?: (
    id: number,
    type:
      | 'cn'
      | 'wifi'
      | 'employee'
      | 'asset'
      | 'alarm'
      | 'ams'
      | 'e-module'
      | 'belt'
  ) => void;
  onOpenHistory?: (
    id: number | string,
    type:
      | 'asset'
      | 'cn'
      | 'wifi'
      | 'wifiLongTerm'
      | 'employee'
      | 'commtracNodeByCn'
      | 'networkDiagnostics'
      | 'alarm'
      | 'alarm_log'
      | 'hazard_ai_detection_log'
      | 'hazard_ai_heatmap'
      | 'amsShortTerm'
      | 'amsLongTerm'
      | 'amsLocation'
      | 'amsEmoduleInstallationHistory'
      | 'amsEmoduleSensorHistory'
      | 'amsEmoduleCalibration'
      | 'beltLocationHistoryReport'
      | 'beltHistoryReport'
  ) => void;
}

const mousePosition = new MousePosition({
  position: 'bottomleft',
  customComponent: ({latlng}) => {
    return latlng.lat || latlng.lng ? (
      <div style={{color: 'black'}}>
        {latlng.lng.toFixed(6)}, {latlng.lat.toFixed(6)}
      </div>
    ) : null;
  },
});

const mapLayers = [
  {id: 'street', label: 'Street map'},
  {id: 'mine', label: 'Mine'},
  {id: 'nodes', label: 'Nodes'},
  {id: 'routing_network', label: 'Routing Network'},
  {id: 'timing_network', label: 'Timing Network'},
  {id: 'new_devices', label: 'New Devices'},
  {id: 'alarms', label: 'Alarm Modules'},
  {id: 'assets', label: 'Assets'},
  {id: 'employees', label: 'Employees'},
  {id: 'employee_history', label: 'Employee History'},
  {id: 'asset_history', label: 'Asset History'},
  {id: 'node_history', label: 'Nodes'},
  {id: 'commtrac_node_by_cn_history', label: 'Employee/Asset History'},
  {id: 'heatmap', label: 'HeatMap'},
  {id: 'e_module', label: 'EModule'},
  {id: 'belts', label: 'Belts'},
  {id: 'belt_location_history', label: 'Belt Location History'},
  {id: 'ams_location', label: 'AMS Location'},
  {
    id: 'ams_emodule_installation_history',
    label: 'AMS E-Module Installation History',
  },
  {id: 'ams_sensor_nodes', label: 'AMS Sensor Nodes'},
] as const;

export type MapLayerId = (typeof mapLayers)[number]['id'];
export type MapLatLangCoordinates = {
  lat: number | null;
  lng: number | null;
};

// eslint-disable-next-line complexity
export const Map = ({
  panel,
  heatMap,
  countByLayer,
  eModules: shownEModules,
  alarmNodes: shownAlarmNodes,
  beltsNodes: shownBeltNodes,
  beltLocationHistoryData: shownBeltLocationHistory,
  commtracNodes: shownCommtracNodes,
  nodes: shownNodes,
  amsSensorNodes: shownAMSSensorNodes,
  employeeHistoryData,
  assetHistoryData,
  commtracNodeByCnEventsData,
  amsLocationHistoryData,
  amsEmoduleInstallationHistoryData,
  selectedMapLayers,
  selectedLevel,
  availableMapLayers: availableMapLayersIds,
  disableEditMode,
  onSelectMapLayers,
  onSelectLevel,
  onGetClickCoordinates,
  onOpenItem,
  onOpenHistory,
  ...props
}: Props) => {
  const _assetsCommtracNodes = useAppSelector(({assets}) => assets.commtrac_nodes);
  const _assetsNodes = useAppSelector(({assets}) => assets.nodes);
  const _assetsAmsEmodules = useAppSelector(({assets}) => assets.ams_emodules);
  const _assetsAmsNodes = useAppSelector(({assets}) => assets.ams_nodes);
  const _assetsBeltNodes = useAppSelector(({assets}) => assets.belt_nodes);
  const _assetsAlarmModules = useAppSelector(({assets}) => assets.alarm_modules);
  const _assetsLayers = useAppSelector(({assets}) => assets.layers);
  const _assetsCompany = useAppSelector(({assets}) => assets.company);
  // Rebuild shape to avoid renaming every reference in the body.
  // Each field is a stable reference: only the subscribed key that changed triggers a re-render.
  const assets = {
    commtrac_nodes: _assetsCommtracNodes,
    nodes: _assetsNodes,
    ams_emodules: _assetsAmsEmodules,
    ams_nodes: _assetsAmsNodes,
    belt_nodes: _assetsBeltNodes,
    alarm_modules: _assetsAlarmModules,
    layers: _assetsLayers,
    company: _assetsCompany,
  };
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);
  const getAmsIconName = useGetAmsIconName();
  const [isEnableAutoFocusMode, setIsEnableAutoFocusMode] = useState(
    fetchMapConfig(panel)?.autoFocus ?? true
  );
  const [map, setMap] = useState<any>(null);
  const [mapZoomLevel, setMapZoomLevel] = useState(12);
  const mapOpacitySetting =
    Number(useConfiguration('global', 'map_opacity')?.value) / 100 || 0.7;

  useEffect(() => {
    setIsEnableAutoFocusMode(fetchMapConfig(panel)?.autoFocus ?? true);
  }, [panel]);

  const [topRightPosition, setTopRightPosition] = useState<any | null>(null);
  //const zones = assets.zones;

  const commtracNodes = shownCommtracNodes ?? assets.commtrac_nodes;
  const nodes = shownNodes ?? assets.nodes;
  const eModules = shownEModules ?? assets?.ams_emodules;
  const beltsNodes = useMemo(
    () =>
      shownBeltNodes?.map((it) => ({
        ...it,
        latitude:
          it?.acknowledged === 0 && !it.acknowledged
            ? topRightPosition
              ? topRightPosition[0]
              : null
            : it.latitude,
        longitude:
          it?.acknowledged === 0 && !it.acknowledged
            ? topRightPosition
              ? topRightPosition[1]
              : null
            : it.longitude,
        pos_lat:
          it?.acknowledged === 0 && !it.acknowledged
            ? topRightPosition
              ? topRightPosition[0]
              : null
            : it.latitude,
        pos_lon:
          it?.acknowledged === 0 && !it.acknowledged
            ? topRightPosition
              ? topRightPosition[1]
              : null
            : it.longitude,
      })) ?? assets?.belt_nodes,
    [shownBeltNodes, topRightPosition, assets?.belt_nodes]
  );

  const beltLocationHistory = shownBeltLocationHistory;

  const alarmNodes = shownAlarmNodes ?? assets.alarm_modules;
  const amsSensorNodes = useMemo(
    () =>
      shownAMSSensorNodes?.map((it) => ({
        ...it,
        latitude:
          it?.ack === '0'
            ? topRightPosition
              ? topRightPosition[0]
              : null
            : it.pos_lat,
        longitude:
          it?.ack === '0'
            ? topRightPosition
              ? topRightPosition[1]
              : null
            : it.pos_lon,
        pos_lat:
          it?.ack === '0'
            ? topRightPosition
              ? topRightPosition[0]
              : null
            : it.pos_lat,
        pos_lon:
          it?.ack === '0'
            ? topRightPosition
              ? topRightPosition[1]
              : null
            : it.pos_lon,
      })) ?? assets?.ams_nodes,
    [shownAMSSensorNodes, topRightPosition, assets?.ams_nodes]
  );

  const acknowledgedOrInvalidPostionNodes = alarmNodes?.filter(
    (it) => it?.acknowledged === 0 || !it?.latitude || !it?.longitude
  );

  // Layers
  const [internalSelectedMapLayers, setInternalSelectedMapLayers] = useState(
    mapLayers.map((i) => i.id)
  );
  const [realFitBounds, setRealFitBound] = useState<LatLngBounds | null>(null);
  const computedSelectedMapLayers = useMemo(() => {
    return (
      _.intersection(selectedMapLayers, availableMapLayersIds) ??
      internalSelectedMapLayers
    );
  }, [selectedMapLayers, availableMapLayersIds, internalSelectedMapLayers]);

  const handleSelectMapLayers = (ids: MapLayerId[]) => {
    if (selectedMapLayers !== undefined) {
      setInternalSelectedMapLayers(ids);
    }
    onSelectMapLayers?.(ids);
  };

  // Levels
  const [internalSelectedLevel, setInternalSelectedLevel] = useState<
    number | null
  >(null);
  const computedSelectedLevel = selectedLevel ?? internalSelectedLevel;
  const selectedLevelOption = useMemo(
    () =>
      computedSelectedLevel
        ? assets.layers?.find((i) => i.id === computedSelectedLevel)
        : null,
    [computedSelectedLevel, assets.layers]
  );

  const handleSelectLevel = (id: number | null) => {
    if (selectedLevel !== undefined) {
      setInternalSelectedLevel(id);
    }
    onSelectLevel?.(id);
  };

  useEffect(() => {
    if (assets?.layers?.length === 1) {
      handleSelectLevel(assets?.layers[0].id);
    }
  }, [assets?.layers]);

  const nodesFixedPosition = useMemo(
    () =>
      nodes.map((it) => ({
        ...it,
        latitude:
          it?.latitude && it?.latitude >= -90 && it?.latitude <= 90
            ? it?.latitude
            : topRightPosition && topRightPosition[0]
              ? topRightPosition[0]
              : 0,
        longitude:
          it?.longitude && it?.longitude >= -180 && it?.longitude <= 180
            ? it?.longitude
            : topRightPosition && topRightPosition[1]
              ? topRightPosition[1]
              : 0,
      })),
    [nodes, topRightPosition]
  );

  const commtracNodeEmployees = useMemo(
    () =>
      commtracNodes
        ?.filter((i) => i.type === 'miner')
        .map((it) => ({
          ...it,
          commtrac_pos_lat:
            it?.commtrac_pos_lat &&
            it?.commtrac_pos_lat >= -90 &&
            it?.commtrac_pos_lat <= 90
              ? it?.commtrac_pos_lat
              : topRightPosition && topRightPosition[0]
                ? topRightPosition[0]
                : 0,
          commtrac_pos_lon:
            it?.commtrac_pos_lon &&
            it?.commtrac_pos_lon >= -180 &&
            it?.commtrac_pos_lon <= 180
              ? it?.commtrac_pos_lon
              : topRightPosition && topRightPosition[1]
                ? topRightPosition[1]
                : 0,
        })),
    [commtracNodes, topRightPosition]
  );

  const commtracNodeAssets = useMemo(
    () =>
      commtracNodes
        ?.filter((i) => i.type === 'asset')
        .map((it) => ({
          ...it,
          commtrac_pos_lat:
            it?.commtrac_pos_lat &&
            it?.commtrac_pos_lat >= -90 &&
            it?.commtrac_pos_lat <= 90
              ? it?.commtrac_pos_lat
              : topRightPosition && topRightPosition[0]
                ? topRightPosition[0]
                : 0,
          commtrac_pos_lon:
            it?.commtrac_pos_lon &&
            it?.commtrac_pos_lon >= -180 &&
            it?.commtrac_pos_lon <= 180
              ? it?.commtrac_pos_lon
              : topRightPosition && topRightPosition[1]
                ? topRightPosition[1]
                : 0,
        })),
    [commtracNodes, topRightPosition]
  );

  const newDevices = useMemo(() => {
    return nodesFixedPosition?.filter((it) => it?.ack === '0');
  }, [nodesFixedPosition]);

  // eslint-disable-next-line complexity
  const itemsGroup = useMemo(() => {
    const group: {
      key: string;
      items: any[];
    }[] = [];
    const allItems: any[] = [];
    if (map) {
      if (
        computedSelectedMapLayers?.includes('employees') &&
        commtracNodeEmployees.length
      ) {
        commtracNodeEmployees.forEach((it) => {
          allItems.push({
            ...it,
            item_type: 'employee',
            item_uniq_id: `employee-${it.id}`,
          });
        });
      }

      if (
        computedSelectedMapLayers?.includes('assets') &&
        commtracNodeAssets.length
      ) {
        commtracNodeAssets.forEach((it) => {
          allItems.push({
            ...it,
            item_type: 'asset',
            item_uniq_id: `asset-${it.id}`,
          });
        });
      }

      if (
        computedSelectedMapLayers?.includes('nodes') &&
        nodesFixedPosition.length
      ) {
        nodesFixedPosition.forEach((it) => {
          allItems.push({
            ...it,
            item_type: 'node',
            item_uniq_id:
              it.node_type === 'communication_node'
                ? `cn-${it.cn_id}`
                : it.node_type === 'wifi_point'
                  ? `wifi-${it.wifi_id}`
                  : `node-${it.id}`,
          });
        });
      }

      if (
        computedSelectedMapLayers?.includes('new_devices') &&
        newDevices.length
      ) {
        newDevices.forEach((it) => {
          allItems.push({
            ...it,
            item_type: 'new_devices',
            item_uniq_id: `new-node-${it.cn_id}`,
          });
        });
      }

      if (computedSelectedMapLayers?.includes('e_module') && eModules.length) {
        eModules.forEach((it) => {
          if (it?.ams?.pos_lat && it?.ams?.pos_lon) {
            allItems.push({
              ...it,
              latitude: it?.ams?.pos_lat,
              longitude: it?.ams?.pos_lon,
              item_type: 'e_module',
              item_uniq_id: `e_module-${it.id}`,
            });
          }
        });
      }

      if (
        computedSelectedMapLayers?.includes('ams_sensor_nodes') &&
        amsSensorNodes.length
      ) {
        amsSensorNodes.forEach((it) => {
          if (it?.pos_lat && it?.pos_lon) {
            allItems.push({
              ...it,
              latitude: it.pos_lat,
              longitude: it.pos_lon,
              item_type: 'ams_sensor',
              item_uniq_id: `ams-sensor-${it.id}`,
            });
          }
        });
      }

      if (computedSelectedMapLayers?.includes('belts') && beltsNodes.length) {
        beltsNodes.forEach((it) => {
          if (it?.latitude && it?.longitude) {
            allItems.push({
              ...it,
              latitude: it.latitude,
              longitude: it.longitude,
              item_type: 'belt',
              item_uniq_id: `belt-${it.id}`,
            });
          }
        });
      }

      if (
        computedSelectedMapLayers?.includes('belt_location_history') &&
        beltLocationHistory.length
      ) {
        beltLocationHistory.forEach((it: any) => {
          if (it?.pos_lat && it?.pos_lon) {
            allItems.push({
              ...it,
              latitude: it.pos_lat,
              longitude: it.pos_lon,
              item_type: 'belt_location_history',
              item_uniq_id: `belt-location-history-${it.id}`,
            });
          }
        });
      }

      // add points to allItems array items for distance comparison
      for (let i = 0; i < allItems.length; i++) {
        allItems[i].groupPoint = map.latLngToContainerPoint(
          allItems[i]?.item_type === 'asset' ||
            allItems[i]?.item_type === 'employee'
            ? new LatLng(
                allItems[i].commtrac_pos_lat ?? 0,
                allItems[i].commtrac_pos_lon ?? 0
              )
            : new LatLng(allItems[i].latitude, allItems[i].longitude)
        );
      }

      const returnGroups = allItems.reduce((prev, curr) => {
        // check prev object
        const pointKeys = Object.keys(prev);

        // if no keys we're on the first point
        // create array and push curr object to it
        // else loop through keys and compare
        if (!pointKeys.length) {
          prev[`${curr.groupPoint.x},${curr.groupPoint.y}`] = [];
          prev[`${curr.groupPoint.x},${curr.groupPoint.y}`].push(curr);
        } else {
          for (let i = 0; i < pointKeys.length; i++) {
            const keyPoints = pointKeys[i].split(',');
            // if a new point is within 10 of any point group, push point to it and break loop
            if (
              curr.groupPoint.distanceTo({x: keyPoints[0], y: keyPoints[1]}) <=
              10
            ) {
              prev[pointKeys[i]].push(curr);
              break;
            }
            // if no match has been found, create array and push to it
            if (i === pointKeys.length - 1) {
              prev[`${curr.groupPoint.x},${curr.groupPoint.y}`] = [];
              prev[`${curr.groupPoint.x},${curr.groupPoint.y}`].push(curr);
            }
          }
        }
        return prev;
      }, {});

      for (const key in returnGroups) {
        group.push({
          key,
          items: returnGroups[key],
        });
      }

      return group;
    } else {
      return [];
    }
  }, [
    nodesFixedPosition,
    commtracNodeEmployees,
    commtracNodeAssets,
    newDevices,
    map,
    mapZoomLevel,
    computedSelectedMapLayers,
    eModules,
    amsSensorNodes,
    beltsNodes,
  ]);

  const employeeHistoryPerLayer = useMemo(
    () =>
      _.chain(assets.layers)
        .keyBy('id')
        .mapValues(() => employeeHistoryData?.filter(() => true))
        .value(),
    [employeeHistoryData, assets.layers]
  );

  const assetHistoryPerLayer = useMemo(
    () =>
      _.chain(assets.layers)
        .keyBy('id')
        .mapValues(() => assetHistoryData?.filter(() => true))
        .value(),
    [assetHistoryData, assets.layers]
  );

  const commtracNodeByCnEventsPerLayer = useMemo(
    () =>
      _.chain(assets.layers)
        .keyBy('id')
        .mapValues(() => commtracNodeByCnEventsData?.filter(() => true))
        .value(),
    [commtracNodeByCnEventsData, assets.layers]
  );

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const amsLocationHistoryPerLayer = useMemo(
    () =>
      _.chain(assets.layers)
        .keyBy('id')
        .mapValues(() => amsLocationHistoryData?.filter(() => true))
        .value(),
    [amsLocationHistoryData, assets.layers]
  );

  // Leeflet
  const isValidCoordinate = (
    lat: string | null | undefined,
    lng: string | null | undefined
  ) => {
    const isLatValid = typeof lat === 'string' && +lat >= -90 && +lat <= 90;
    const isLngValid = typeof lng === 'string' && +lng >= -180 && +lng <= 180;
    return isLatValid && isLngValid;
  };

  const canUseCompanyBounds = useMemo(() => {
    return (
      isValidCoordinate(
        assets.company?.miny ?? null,
        assets.company?.minx ?? null
      ) &&
      isValidCoordinate(
        assets.company?.maxy ?? null,
        assets.company?.maxx ?? null
      )
    );
  }, [assets.company]);

  const companyBounds = useMemo(() => {
    return assets.company && canUseCompanyBounds
      ? latLngBounds(
          [
            assets.company?.minx ? +assets.company?.minx : 0,
            assets.company?.miny ? +assets.company?.miny : 0,
          ],
          [
            assets.company?.maxx ? +assets.company?.maxx : 0,
            assets.company?.maxy ? +assets.company?.maxy : 0,
          ]
        )
      : undefined;
  }, [assets.company]);

  const {ref, width, height} = useComponentSize();
  const minLayerBounds = useMemo(
    () =>
      selectedLevelOption && selectedLevelOption.map
        ? latLngBounds(
            [+selectedLevelOption.map?.minx, +selectedLevelOption.map?.miny],
            [+selectedLevelOption.map?.maxx, +selectedLevelOption.map?.maxy]
          )
        : companyBounds,
    [selectedLevel]
  );

  const MapEvents = useMemo(() => {
    const MapEventsComponent = () => {
      useMapEvents({
        click(e) {
          onGetClickCoordinates?.(e.latlng);
        },

        moveend() {
          if (map) {
            setMapZoomLevel(map.getZoom());
            saveMapConfig(map, panel, isEnableAutoFocusMode);
          }
        },
      });
      return null;
    };
    return MapEventsComponent;
  }, [onGetClickCoordinates, map, panel, isEnableAutoFocusMode]);

  useEffect(() => {
    const updateMarkerPosition = () => {
      if (selectedLevelOption) {
        const topRightPoint = [
          selectedLevelOption?.map
            ? +selectedLevelOption?.map.maxx
            : assets.company?.maxx ?? 90,
          selectedLevelOption?.map
            ? +selectedLevelOption?.map.maxy
            : assets.company?.maxy ?? 180,
        ];
        setTopRightPosition((prev: any) => {
          if (prev && _.isEqual(prev, topRightPoint)) {
            return prev;
          }
          return topRightPoint;
        });
      } else {
        setTopRightPosition((prev: any) => {
          if (prev === null) {
            return prev;
          }
          return null;
        });
      }
    };

    if (map) {
      map.on('resize', updateMarkerPosition);
      updateMarkerPosition();
    }
    return () => {
      if (map) {
        map.off('resize', updateMarkerPosition);
      }
    };
  }, [map, selectedLevelOption]);

  // map icons
  const configurations = useConfigurations();
  const getAlarmLogIcon = (item: AlarmModuleNode) => {
    let color: string | number | undefined;
    if (item?.status === 'active') {
      color = 'blue';
    }
    if (item?.status === 'inactive') {
      color = 'gray';
    }
    if (item?.e_701) {
      color =
        configurations[
          ALARM_MODULE_COLORS[ALARM_MODULE_TYPES.ALARM_NO_HEARTBEAT] ?? ''
        ]?.value ?? 'default';
    }
    if (item?.e_702) {
      color =
        configurations[
          ALARM_MODULE_COLORS[ALARM_MODULE_TYPES.ALARM_NEW_MODULE_DETECTED] ??
            ''
        ]?.value ?? 'gray';
    }
    if (item?.e_704) {
      color =
        configurations[
          ALARM_MODULE_COLORS[ALARM_MODULE_TYPES.ALARM_ENABLED_WARNING_TYPES] ??
            ''
        ]?.value ?? 'yellow';
    }
    if (item?.e_703) {
      color =
        configurations[
          ALARM_MODULE_COLORS[ALARM_MODULE_TYPES.ALARM_ENABLED_ALARM_TYPES] ??
            ''
        ]?.value ?? 'red';
    }

    return COLORED_LEAFLET_ICONS.alarm[color ?? 'gray'];
  };

  const alertLevel =
    configurations['commtrac.communication_node_rssi_alert_level']?.value;
  const alarmLevel =
    configurations['commtrac.communication_node_rssi_alarm_level']?.value;

  const getEmployeeHistoryIcon = () => {
    return COLORED_LEAFLET_ICONS.miners.default;
  };

  const getAssetHistoryIcon = () => {
    return COLORED_LEAFLET_ICONS.assetEvents.default;
  };

  const getCommtracNodeByCnHistoryIcon = (item: CommtracNodeByCnEvent) => {
    if (item.type === 0) {
      return COLORED_LEAFLET_ICONS.assets.default;
    }
    return COLORED_LEAFLET_ICONS.miners.default;
  };

  const getAMSLocationHistoryIcon = (type: number | null) => {
    return COLORED_LEAFLET_ICONS[getAmsIconName(type) as iconName].blue;
    /*switch (type) {
      case 'AMB':
        return COLORED_LEAFLET_ICONS.AMB.blue;
      case 'ANA':
        return COLORED_LEAFLET_ICONS.ANA.blue;
      case 'CH4':
        return COLORED_LEAFLET_ICONS.CH4.blue;
      case 'CL2':
        return COLORED_LEAFLET_ICONS.CL2.blue;
      case 'CO':
        return COLORED_LEAFLET_ICONS.CO.blue;
      case 'H2':
        return COLORED_LEAFLET_ICONS.H2.blue;
      case 'H2S':
        return COLORED_LEAFLET_ICONS.H2S.blue;
      case 'NO':
        return COLORED_LEAFLET_ICONS.NO.blue;
      case 'NO2':
        return COLORED_LEAFLET_ICONS.NO2.blue;
      case 'O2':
        return COLORED_LEAFLET_ICONS.O2.blue;
      case 'SO2':
        return COLORED_LEAFLET_ICONS.SO2.blue;
      case 'SW':
        return COLORED_LEAFLET_ICONS.SW.blue;
      default:
        return COLORED_LEAFLET_ICONS.unknown.health;
    }*/
  };

  const getBeltLocationHistoryIcon = () => {
    return COLORED_LEAFLET_ICONS.belts.healthy;
  };

  useEffect(() => {
    if (map) {
      mousePosition.addTo(map);
    }
    return () => {
      mousePosition.remove();
    };
  }, [map]);

  useEffect(() => {
    map?.invalidateSize();
  }, [width, height]);

  // Layer and level
  const [layerMenuAnchorEl, setLayerMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const layerMenuOpen = Boolean(layerMenuAnchorEl);

  const availableMapLayers = useMemo(() => {
    if (
      availableMapLayersIds?.length &&
      selectedLevelOption &&
      !selectedLevelOption.map
    ) {
      return mapLayers.filter(
        (i) => availableMapLayersIds.includes(i.id) && i.id !== 'mine'
      );
    } else if (availableMapLayersIds?.length) {
      return mapLayers.filter((i) => availableMapLayersIds.includes(i.id));
    }
    return mapLayers;
  }, [availableMapLayersIds, selectedLevelOption]);

  const selectedLayerCount = useMemo(() => {
    if (!selectedLevelOption?.map) {
      return computedSelectedMapLayers.filter((el) => el !== 'mine').length;
    }
    return computedSelectedMapLayers.length;
  }, [computedSelectedMapLayers, selectedLevelOption]);

  const availableLayerCount = useMemo(() => {
    if (!selectedLevelOption?.map) {
      return availableMapLayers.filter((el) => el.id !== 'mine').length;
    }
    return availableMapLayers.length;
  }, [availableMapLayers, selectedLevelOption]);

  const checkedFitBoundRange = (
    totalMarkers: number,
    maxLat: number,
    maxLng: number,
    minLat: number,
    minLng: number
  ) => {
    if (computedSelectedMapLayers?.includes('alarms')) {
      alarmNodes?.forEach((it: AlarmModuleNode) => {
        minLng = Math.min(minLng, +(it?.longitude ?? 0));
        maxLng = Math.max(maxLng, +(it?.longitude ?? 0));
        minLat = Math.min(minLat, +(it?.latitude ?? 0));
        maxLat = Math.max(maxLat, +(it?.latitude ?? 0));
      });
      totalMarkers += alarmNodes.length;
    }

    if (computedSelectedMapLayers?.includes('employees')) {
      commtracNodeEmployees?.forEach((it) => {
        minLng = Math.min(minLng, +(it?.commtrac_pos_lon ?? 0));
        maxLng = Math.max(maxLng, +(it?.commtrac_pos_lon ?? 0));
        minLat = Math.min(minLat, +(it?.commtrac_pos_lat ?? 0));
        maxLat = Math.max(maxLat, +(it?.commtrac_pos_lat ?? 0));
      });
      totalMarkers += commtracNodeEmployees.length;
    }

    if (computedSelectedMapLayers?.includes('assets')) {
      commtracNodeAssets?.forEach((it) => {
        minLng = Math.min(minLng, +(it?.commtrac_pos_lon ?? 0));
        maxLng = Math.max(maxLng, +(it?.commtrac_pos_lon ?? 0));
        minLat = Math.min(minLat, +(it?.commtrac_pos_lat ?? 0));
        maxLat = Math.max(maxLat, +(it?.commtrac_pos_lat ?? 0));
      });
      totalMarkers += commtracNodeAssets.length;
    }

    if (computedSelectedMapLayers?.includes('nodes')) {
      nodesFixedPosition?.forEach((it) => {
        minLng = Math.min(minLng, +(it?.longitude ?? 0));
        maxLng = Math.max(maxLng, +(it?.longitude ?? 0));
        minLat = Math.min(minLat, +(it?.latitude ?? 0));
        maxLat = Math.max(maxLat, +(it?.latitude ?? 0));
      });
      totalMarkers += nodesFixedPosition.length;
    }

    if (computedSelectedMapLayers?.includes('employee_history')) {
      employeeHistoryData?.forEach((it) => {
        minLng = Math.min(
          minLng,
          +(it.longitude || (selectedLevelOption?.map?.maxy ?? 0))
        );
        maxLng = Math.max(
          maxLng,
          +(it.longitude || (selectedLevelOption?.map?.maxy ?? 0))
        );
        minLat = Math.min(
          minLat,
          +(it.latitude || (selectedLevelOption?.map?.maxx ?? 0))
        );
        maxLat = Math.max(
          maxLat,
          +(it.latitude || (selectedLevelOption?.map?.maxx ?? 0))
        );
      });
      totalMarkers += employeeHistoryData?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('asset_history')) {
      assetHistoryData?.forEach((it) => {
        minLng = Math.min(
          minLng,
          +(it.longitude || (selectedLevelOption?.map?.maxy ?? 0))
        );
        maxLng = Math.max(
          maxLng,
          +(it.longitude || (selectedLevelOption?.map?.maxy ?? 0))
        );
        minLat = Math.min(
          minLat,
          +(it.latitude || (selectedLevelOption?.map?.maxx ?? 0))
        );
        maxLat = Math.max(
          maxLat,
          +(it.latitude || (selectedLevelOption?.map?.maxx ?? 0))
        );
      });
      totalMarkers += assetHistoryData?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('commtrac_node_by_cn_history')) {
      commtracNodeByCnEventsData?.forEach((it) => {
        minLng = Math.min(
          minLng,
          +(it.pos_lon || (selectedLevelOption?.map?.maxy ?? 0))
        );
        maxLng = Math.max(
          maxLng,
          +(it.pos_lon || (selectedLevelOption?.map?.maxy ?? 0))
        );
        minLat = Math.min(
          minLat,
          +(it.pos_lat || (selectedLevelOption?.map?.maxx ?? 0))
        );
        maxLat = Math.max(
          maxLat,
          +(it.pos_lat || (selectedLevelOption?.map?.maxx ?? 0))
        );
      });
      totalMarkers += commtracNodeByCnEventsData?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('ams_location')) {
      amsLocationHistoryData?.forEach((it) => {
        minLng = Math.min(
          minLng,
          +(it.pos_lon || (selectedLevelOption?.map?.maxy ?? 0))
        );
        maxLng = Math.max(
          maxLng,
          +(it.pos_lon || (selectedLevelOption?.map?.maxy ?? 0))
        );
        minLat = Math.min(
          minLat,
          +(it.pos_lat || (selectedLevelOption?.map?.maxx ?? 0))
        );
        maxLat = Math.max(
          maxLat,
          +(it.pos_lat || (selectedLevelOption?.map?.maxx ?? 0))
        );
      });
      totalMarkers += amsLocationHistoryData?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('ams_sensor_nodes')) {
      amsSensorNodes?.forEach((it) => {
        minLng = Math.min(minLng, +(it?.pos_lon ?? 0));
        maxLng = Math.max(maxLng, +(it?.pos_lon ?? 0));
        minLat = Math.min(minLat, +(it?.pos_lat ?? 0));
        maxLat = Math.max(maxLat, +(it?.pos_lat ?? 0));
      });
      totalMarkers += amsSensorNodes?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('belts')) {
      beltsNodes?.forEach((it) => {
        minLng = Math.min(minLng, +(it?.longitude ?? 0));
        maxLng = Math.max(maxLng, +(it?.longitude ?? 0));
        minLat = Math.min(minLat, +(it?.latitude ?? 0));
        maxLat = Math.max(maxLat, +(it?.latitude ?? 0));
      });
      totalMarkers += beltsNodes?.length ?? 0;
    }

    if (computedSelectedMapLayers?.includes('belt_location_history')) {
      beltLocationHistory?.forEach((it: any) => {
        minLng = Math.min(minLng, +(it?.pos_lon ?? 0));
        maxLng = Math.max(maxLng, +(it?.pos_lon ?? 0));
        minLat = Math.min(minLat, +(it?.pos_lat ?? 0));
        maxLat = Math.max(maxLat, +(it?.pos_lat ?? 0));
      });
      totalMarkers += beltLocationHistory.length ?? 0;
    }

    return {totalMarkers, maxLat, maxLng, minLat, minLng};
  };

  const fitBoundRange = useMemo(() => {
    let minLng: number = 180;
    let maxLng: number = -180;
    let minLat: number = 90;
    let maxLat: number = -90;
    let totalMarkers: number = 0;

    const markersData = checkedFitBoundRange(
      totalMarkers,
      maxLat,
      maxLng,
      minLat,
      minLng
    );
    totalMarkers = markersData.totalMarkers;
    maxLat = markersData.maxLat;
    maxLng = markersData.maxLng;
    minLat = markersData.minLat;
    minLng = markersData.minLng;

    const mapConfig = fetchMapConfig(panel);
    if (totalMarkers < 1) {
      minLng = Number(assets?.company?.miny ?? -180);
      maxLng = Number(assets?.company?.maxy ?? 180);
      minLat = Number(assets?.company?.minx ?? -90);
      maxLat = Number(assets?.company?.maxx ?? 90);
    }
    if (isEnableAutoFocusMode || !mapConfig) {
      if (acknowledgedOrInvalidPostionNodes.length > 0 || totalMarkers < 1) {
        return (
          minLayerBounds ?? latLngBounds([minLat, minLng], [maxLat, maxLng])
        );
      }
    } else if (mapConfig && !isEnableAutoFocusMode) {
      return latLngBounds(mapConfig?.bounds[0], mapConfig?.bounds[1]);
    }

    return latLngBounds([minLat, minLng], [maxLat, maxLng]);
  }, [
    assets.company,
    minLayerBounds,
    computedSelectedMapLayers,
    commtracNodeEmployees,
    commtracNodeAssets,
    nodesFixedPosition,
    alarmNodes,
    acknowledgedOrInvalidPostionNodes,
    employeeHistoryData,
    assetHistoryData,
    commtracNodeByCnEventsData,
    amsLocationHistoryData,
    amsSensorNodes,
    beltsNodes,
    isEnableAutoFocusMode,
  ]);

  useEffect(() => {
    setRealFitBound((prev) => {
      if (_.isEqual(prev, fitBoundRange)) {
        return prev;
      }

      return fitBoundRange;
    });
  }, [fitBoundRange]);

  useEffect(() => {
    if (map && realFitBounds) {
      map.fitBounds(realFitBounds);
    }
  }, [map, realFitBounds]);

  useEffect(() => {
    if (map) {
      saveMapConfig(map, panel, isEnableAutoFocusMode);
    }
  }, [isEnableAutoFocusMode]);

  // Timing & Routing

  const cnNodes = useMemo(
    () => nodesFixedPosition?.filter((it) => !!it?.cn_id),
    [nodesFixedPosition]
  );

  const timingNodesGroup = useMemo(() => {
    if (computedSelectedMapLayers.includes('timing_network')) {
      const timingGroup = findTimingOrRoutingGroup(
        cnNodes,
        'timing',
        +alertLevel,
        +alarmLevel
      );
      return timingGroup;
    }
    return [];
  }, [cnNodes, computedSelectedMapLayers]);

  const routingNodesGroup = useMemo(() => {
    if (computedSelectedMapLayers.includes('routing_network')) {
      const routingGroup = findTimingOrRoutingGroup(
        cnNodes,
        'routing',
        +alertLevel,
        +alarmLevel
      );
      return routingGroup;
    }
    return [];
  }, [cnNodes, computedSelectedMapLayers]);

  const mapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
    }),
    []
  );

  const mapContainerCenter = useMemo(() => [0, 0] as [number, number], []);

  return (
    <Box
      ref={ref}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      className="123-cl"
      {...props}
    >
      {selectedLevelOption ? (
        <Box
          width="100%"
          height="100%"
          position="relative"
          sx={{
            '.leaflet-top': {top: 45},
            '.leaflet-tooltip-pane': {zIndex: 750},
            '.duplicate.icon': {
              width: '32px !important',
              height: '37px !important',
              span: {
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: 500,
              },
            },
          }}
        >
          <Box
            position="absolute"
            zIndex={500}
            top={11}
            left={11}
            display="flex"
          >
            <Button
              variant="contained"
              size="small"
              sx={{
                p: 0.5,
                borderRadius: 2,
                mr: 1,
                minWidth: 'auto',
                color: '#000',
                backgroundColor: '#FFF',
              }}
              onClick={() => handleSelectLevel(null)}
            >
              <ArrowBackIcon />
            </Button>

            <Button
              variant="contained"
              sx={{
                py: 0.5,
                whiteSpace: 'nowrap',
                color: '#000',
                backgroundColor: '#FFF',
                zIndex: 1,
              }}
              onClick={(event) => {
                setLayerMenuAnchorEl(event.currentTarget);
              }}
            >
              {selectedLevelOption?.name} ({selectedLayerCount}/
              {availableLayerCount} layers)
            </Button>

            <Menu
              anchorEl={layerMenuAnchorEl}
              open={layerMenuOpen}
              onBackdropClick={() => setLayerMenuAnchorEl(null)}
            >
              <Typography
                textTransform="uppercase"
                fontWeight={400}
                width={300}
                color="text.disabled"
                p={2}
                pb={1}
              >
                Layers
              </Typography>

              {availableMapLayers.map((i) => (
                <MenuItem
                  key={i.id}
                  selected={computedSelectedMapLayers.includes(i.id)}
                  onClick={() => {
                    if (computedSelectedMapLayers.includes(i.id)) {
                      handleSelectMapLayers(
                        computedSelectedMapLayers.filter((l) => l !== i.id)
                      );
                    } else {
                      handleSelectMapLayers([
                        ...computedSelectedMapLayers,
                        i.id,
                      ]);
                    }
                  }}
                >
                  {i.label}
                </MenuItem>
              ))}

              <Divider />

              <Typography
                textTransform="uppercase"
                fontWeight={400}
                width={300}
                color="text.disabled"
                p={2}
                pb={1}
              >
                Levels
              </Typography>

              {assets.layers.map((i) => (
                <MenuItem
                  key={i.id}
                  selected={computedSelectedLevel === i.id}
                  onClick={() => {
                    handleSelectLevel(i.id);
                    setLayerMenuAnchorEl(null);
                  }}
                >
                  {i.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <MuiTooltip title="Map Zoom">
            <Button
              variant="contained"
              size="small"
              sx={{
                position: 'absolute',
                zIndex: 500,
                top: 130,
                left: 11,
                p: 0.5,
                borderRadius: 2,
                minWidth: 'auto',
                color: '#000',
                backgroundColor: '#FFF',
              }}
              onClick={() => {
                if (minLayerBounds) {
                  const maxLng = minLayerBounds.getEast();
                  const minLng = minLayerBounds.getWest();
                  const maxLat = minLayerBounds.getNorth();
                  const minLat = minLayerBounds.getSouth();
                  map.fitBounds(
                    latLngBounds([minLat, minLng], [maxLat, maxLng])
                  );
                } else if (selectedLevelOption && selectedLevelOption.map) {
                  map.fitBounds(
                    latLngBounds(
                      [
                        +selectedLevelOption.map?.minx,
                        +selectedLevelOption.map?.miny,
                      ],
                      [
                        +selectedLevelOption.map?.maxx,
                        +selectedLevelOption.map?.maxy,
                      ]
                    )
                  );
                }
              }}
            >
              <LocationSearchingIcon />
            </Button>
          </MuiTooltip>

          <MuiTooltip title="Auto Zoom">
            <Button
              variant="contained"
              size="small"
              sx={{
                position: 'absolute',
                zIndex: 500,
                top: 170,
                left: 11,
                p: 0.5,
                borderRadius: 2,
                minWidth: 'auto',
                color: '#000',
                backgroundColor: !isEnableAutoFocusMode ? '#FFF' : 'main',
              }}
              onClick={() => setIsEnableAutoFocusMode(!isEnableAutoFocusMode)}
            >
              <ZoomInMapTwoTone />
            </Button>
          </MuiTooltip>

          <MapContainer
            ref={setMap}
            // set boudds using effect, but need to set center and zoom to initialize
            //bounds={fitBoundRange}
            center={mapContainerCenter}
            zoom={12}
            maxZoom={18}
            style={mapContainerStyle}
          >
            <>
              <MapEvents />

              {computedSelectedMapLayers?.includes('street') && (
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  zIndex={0}
                />
              )}

              {computedSelectedMapLayers?.includes('mine') &&
                selectedLevelOption.map && (
                  <TileLayer
                    url={`${mineMapBaseUrl}/${selectedLevelOption.map.company_id}/${selectedLevelOption.map.path}/map/{z}/{x}/{-y}.png`}
                    //
                    opacity={mapOpacitySetting}
                    zIndex={1}
                  />
                )}

              {computedSelectedMapLayers?.includes('timing_network') &&
                timingNodesGroup?.map((it, index: number) => {
                  const reversedGroup = [...it].reverse();
                  return (
                    <Fragment key={index}>
                      {reversedGroup.map((_, i, array) => {
                        const curr = array[i];
                        const color =
                          curr?.timing_connection_status === 'good'
                            ? 'rgb(0, 145, 0)'
                            : curr?.timing_connection_status === 'alert'
                              ? 'rgb(240, 240, 0)'
                              : 'rgb(200, 0, 0)';
                        const next = array[i + 1];
                        if (
                          !curr?.latitude ||
                          !curr?.longitude ||
                          !next?.latitude ||
                          !next?.longitude
                        ) {
                          return null;
                        }

                        return (
                          <ArrowLine
                            key={`${index}-${i}`}
                            start={[curr.latitude, curr.longitude]}
                            end={[next.latitude, next.longitude]}
                            icon={createArrowIcon(color)}
                            color={color}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}

              {computedSelectedMapLayers?.includes('heatmap') &&
                heatMap &&
                heatMap?.length > 0 &&
                heatMap
                  ?.filter((it) => !!it.isSelected)
                  .map((it, index: number) => {
                    return (
                      <HeatMapLayer
                        id={`map-${index}`}
                        data={it.address_points}
                        options={{
                          radius: it.configuration?.radius ?? 25,
                          blur: it.configuration?.blur ?? 15,
                          max: 0.001,
                          gadient: it.configuration?.color_gradients
                            ? generatePickColorSet(
                                it.configuration.color_gradients
                              )
                            : null,
                        }}
                        key={index}
                        map={map}
                      />
                    );
                  })}

              {computedSelectedMapLayers?.includes('routing_network') &&
                routingNodesGroup?.map((it, index: number) => {
                  const reversedGroup = [...it].reverse();
                  return (
                    <Fragment key={index}>
                      {reversedGroup.map((_, i, array) => {
                        const curr = array[i];
                        const color =
                          curr?.routing_connection_status === 'good'
                            ? 'rgb(0, 145, 0)'
                            : curr?.routing_connection_status === 'alert'
                              ? 'rgb(240, 240, 0)'
                              : 'rgb(200, 0, 0)';
                        const next = array[i + 1];
                        if (
                          !curr?.latitude ||
                          !curr?.longitude ||
                          !next?.latitude ||
                          !next?.longitude
                        ) {
                          return null;
                        }

                        return (
                          <ArrowLine
                            key={`${index}-${i}`}
                            start={[curr.latitude, curr.longitude]}
                            end={[next.latitude, next.longitude]}
                            icon={createArrowIcon(color)}
                            color={color}
                            dashed={true}
                            shift={
                              computedSelectedMapLayers?.includes(
                                'timing_network'
                              )
                                ? 6
                                : 0
                            }
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}

              {(computedSelectedMapLayers.includes('employees') ||
                computedSelectedMapLayers.includes('assets') ||
                computedSelectedMapLayers.includes('nodes') ||
                computedSelectedMapLayers.includes('new_devices') ||
                computedSelectedMapLayers.includes('e_module') ||
                computedSelectedMapLayers.includes('ams_sensor_nodes') ||
                computedSelectedMapLayers.includes('belts')) &&
                itemsGroup?.map((i, index: number) => {
                  return (
                    <TempItemMarker
                      topRightPosition={topRightPosition}
                      key={`item-${index}`}
                      i={i}
                      type={i.items[0].item_type}
                      onOpenItem={onOpenItem}
                      onOpenHistory={onOpenHistory}
                    />
                  );
                })}

              {computedSelectedMapLayers.includes('alarms') &&
                alarmNodes
                  ?.filter(
                    (it) => it?.acknowledged && it?.latitude && it?.longitude
                  )
                  .map((i: AlarmModuleNode) => (
                    <Marker
                      key={`node-${i.id}`}
                      position={[
                        i?.latitude ?? minLayerBounds?.getNorthEast()?.lat ?? 0,
                        i?.longitude ??
                          minLayerBounds?.getNorthEast()?.lng ??
                          0,
                      ]}
                      icon={getAlarmLogIcon(i)}
                      interactive
                      eventHandlers={{
                        click: (event) => event.target.openPopup(),
                        popupopen: (event) => {
                          event.target._tooltip.options.opacity = 0;
                        },
                        popupclose: (event) => {
                          event.target._tooltip.options.opacity = 0.9;
                        },
                      }}
                    >
                      <Popup interactive autoClose={false} offset={[0, -32]}>
                        <AlarmTooltipContent
                          item={i}
                          disableEditMode={disableEditMode}
                          onOpenItem={() => {
                            onOpenItem?.(i.id, 'alarm');
                          }}
                          onOpenHistory={() => {
                            onOpenHistory?.(i.id, 'alarm');
                          }}
                        />
                      </Popup>

                      <Tooltip
                        direction="top"
                        offset={[0, -34]}
                        pane="popupPane"
                      >
                        <AlarmTooltipContent
                          item={i}
                          disableEditMode={disableEditMode}
                          onOpenItem={() => {
                            onOpenItem?.(i.id, 'alarm');
                          }}
                          onOpenHistory={() => {
                            onOpenHistory?.(i.id, 'alarm');
                          }}
                        />
                      </Tooltip>
                    </Marker>
                  ))}

              {computedSelectedMapLayers.includes('alarms') &&
                computedSelectedMapLayers.includes('mine') &&
                acknowledgedOrInvalidPostionNodes.map((i: AlarmModuleNode) => {
                  return (
                    <Marker
                      key={`node-${i.id}`}
                      position={topRightPosition ? topRightPosition : [0, 0]}
                      icon={getAlarmLogIcon(i)}
                      interactive
                      eventHandlers={{
                        click: (event) => event.target.openPopup(),
                        popupopen: (event) => {
                          event.target._tooltip.options.opacity = 0;
                        },
                        popupclose: (event) => {
                          event.target._tooltip.options.opacity = 0.9;
                        },
                      }}
                    >
                      <Popup interactive autoClose={false} offset={[0, -32]}>
                        <AlarmTooltipContent
                          item={i}
                          disableEditMode={disableEditMode}
                          onOpenItem={() => {
                            onOpenItem?.(i.id, 'alarm');
                          }}
                          onOpenHistory={() => {
                            onOpenHistory?.(i.id, 'alarm');
                          }}
                        />
                      </Popup>

                      <Tooltip
                        direction="top"
                        offset={[0, -34]}
                        pane="popupPane"
                      >
                        <AlarmTooltipContent
                          item={i}
                          disableEditMode={disableEditMode}
                          onOpenItem={() => {
                            onOpenItem?.(i.id, 'alarm');
                          }}
                          onOpenHistory={() => {
                            onOpenHistory?.(i.id, 'alarm');
                          }}
                        />
                      </Tooltip>
                    </Marker>
                  );
                })}

              {computedSelectedMapLayers.includes('employee_history') &&
                employeeHistoryData?.map((i) => (
                  <Marker
                    key={`commtrac-node-history-employee-${getCommtracNodeHistoryId(i)}`}
                    position={[
                      +(i.latitude || selectedLevelOption.map?.maxx),
                      +(i.longitude || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getEmployeeHistoryIcon()}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <CommtracNodeHistoryTooltipContent
                        timestamp={i.timestamp}
                      />
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <CommtracNodeHistoryTooltipContent
                        timestamp={i.timestamp}
                      />
                    </Tooltip>
                  </Marker>
                ))}

              {computedSelectedMapLayers.includes('asset_history') &&
                assetHistoryData?.map((i) => (
                  <Marker
                    key={`commtrac-node-history-asset-${getCommtracNodeHistoryId(i)}`}
                    position={[
                      +(i.latitude || selectedLevelOption.map?.maxx),
                      +(i.longitude || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getAssetHistoryIcon()}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <CommtracNodeHistoryTooltipContent
                        timestamp={i.timestamp}
                      />
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <CommtracNodeHistoryTooltipContent
                        timestamp={i.timestamp}
                      />
                    </Tooltip>
                  </Marker>
                ))}

              {computedSelectedMapLayers.includes(
                'commtrac_node_by_cn_history'
              ) &&
                commtracNodeByCnEventsData?.map((i) => (
                  <Marker
                    key={`commtrac-node-by-cn-history-${getCommtracNodeByCnHistoryId(i)}`}
                    position={[
                      +(i.pos_lat || selectedLevelOption.map?.maxx),
                      +(i.pos_lon || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getCommtracNodeByCnHistoryIcon(i)}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <CommtracNodeHistoryTooltipContent timestamp={i.date} />
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <CommtracNodeHistoryTooltipContent timestamp={i.date} />
                    </Tooltip>
                  </Marker>
                ))}

              {computedSelectedMapLayers.includes('ams_location') &&
                amsLocationHistoryData?.map((i) => (
                  <Marker
                    key={`ams-location-history-${getAMSLocationHistoryId(i)}`}
                    position={[
                      +(i.pos_lat || selectedLevelOption.map?.maxx),
                      +(i.pos_lon || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getAMSLocationHistoryIcon(i.type_id)}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>{i.date}</Box>
                      </Box>
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>{i.date}</Box>
                      </Box>
                    </Tooltip>
                  </Marker>
                ))}

              {computedSelectedMapLayers.includes(
                'ams_emodule_installation_history'
              ) &&
                amsEmoduleInstallationHistoryData?.map((i) => (
                  <Marker
                    key={`ams-emodule-installation-history-${i.id}`}
                    position={[
                      +(i.pos_lat || selectedLevelOption.map?.maxx),
                      +(i.pos_lon || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getAMSLocationHistoryIcon(i.type_id)}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>ID: {i.commtrac_external_id}</Box>
                        <Box>Name: {i.name}</Box>
                        <Box>Date: {i.date}</Box>
                      </Box>
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>{i.name}</Box>
                      </Box>
                    </Tooltip>
                  </Marker>
                ))}

              {computedSelectedMapLayers.includes('belt_location_history') &&
                beltLocationHistory?.map((i: any) => (
                  <Marker
                    key={`belt-location-history-${i.id}`}
                    position={[
                      +(i.pos_lat || selectedLevelOption.map?.maxx),
                      +(i.pos_lon || selectedLevelOption.map?.maxy),
                    ]}
                    icon={getBeltLocationHistoryIcon()}
                    eventHandlers={{
                      click: (event) => event.target.openPopup(),
                      popupopen: (event) => {
                        event.target._tooltip.options.opacity = 0;
                      },
                      popupclose: (event) => {
                        event.target._tooltip.options.opacity = 0.9;
                      },
                    }}
                  >
                    <Popup interactive autoClose={false} offset={[0, -32]}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>
                          Network ID:{' '}
                          {
                            // eslint-disable-next-line no-bitwise
                            i.commtrac_external_id & beltAddressMask
                          }
                        </Box>
                        <Box>Closest Node Name: {i.strongest_cn_name}</Box>
                        <Box>Timestamp: {i.date}</Box>
                      </Box>
                    </Popup>

                    <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box>{`Timestamp: ${i.date}`}</Box>
                      </Box>
                    </Tooltip>
                  </Marker>
                ))}
            </>
          </MapContainer>
        </Box>
      ) : (
        <Box
          overflow="auto"
          height="auto"
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {assets.layers.map((layer) => (
            <Box
              key={layer.id}
              //height={`${100 / assets.layers.length}%`}
              //minHeight={140}
              //flexGrow={1}
              padding="10px 0"
              width="100%"
              position="relative"
              boxShadow="0px 11px 15px -7px rgb(0 0 0 / 20%), 0px 24px 38px 3px rgb(0 0 0 / 14%), 0px 9px 46px 8px rgb(0 0 0 / 12%)"
              bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
              sx={{cursor: 'pointer'}}
              onClick={() => handleSelectLevel(layer.id)}
            >
              <Box textAlign="center">
                <Box fontSize={22} fontWeight={700}>
                  {layer.name}
                </Box>

                {countByLayer?.map((count) => (
                  <Box key={`${count.device_name}`}>
                    {count.device_name}: {count.count?.[layer.id] ?? 0}
                  </Box>
                ))}

                {employeeHistoryPerLayer[layer.id] && (
                  <Box>
                    Emplyee History: {employeeHistoryPerLayer[layer.id]?.length}
                  </Box>
                )}
                {assetHistoryPerLayer[layer.id] && (
                  <Box>
                    Asset History: {assetHistoryPerLayer[layer.id]?.length}
                  </Box>
                )}
                {commtracNodeByCnEventsPerLayer[layer.id] && (
                  <Box>
                    Employees/Assets History:{' '}
                    {commtracNodeByCnEventsPerLayer[layer.id]?.length}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
